import { IconButton, Loader, Link, Button } from '@deque/cauldron-react';
import {
  universalExport,
  mapIssueToSavedIssue,
  getFriendlyFilename,
  type UniversalIssueWithScreenshot,
  mapAxeExtensionIssueToUniversalIssue,
  manifestUtils
} from '@deque/axe-devtools-issues-utils';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import pkg from '../../../../package.json';
import PageTitle from '../../common/components/PageTitle';
import CopyToClipboard from '../../common/components/CopyToClipboard';
import TestDetails from '../components/TestDetails';
import { useAuthContext } from '../../common/contexts/auth';
import { useTest } from '../contexts/Test';
import useIssuesFilter, {
  getBaseFilters,
  getSearchParamsFromFilters
} from '../../common/hooks/useIssuesFilter';
import rulesets from '@deque/accessibility-standards';
import styles from './Test.css';
import useMediaQuery from '../../common/hooks/useMediaQuery';
import { isNotFoundError } from '../../common/utils/errors';
import useSharedTestAnalyticsData from '../hooks/useSharedTestAnalyticsData';
import { useAxeDevtoolsProAnalytics } from '../../common/contexts/analytics';
import ShareTestBetaFeatureToast from '../components/ShareTestBetaFeatureToast';
import { getIssuesWithUniversalMatches } from '../utils/getIssuesWithUniversalMatches';
import { getApiServerUrl } from '../../common/utils/get-api-server-url';
import {
  type HttpError,
  type Manifests,
  sendTest
} from '../../common/api-client';
import { useFeatureFlagState } from '../../common/contexts/featureFlags';
import useCurrentSubscription from '../../common/hooks/useCurrentSubscription';
import { ProductSlugs } from '@deque/billing-utils';
import SendTestModal from '../components/SendTestModal';
import { useGlobalToast } from '../../common/contexts/globalToast';
import useIntegrationConnections from '../../app/hooks/useIntegrationConnections';
import analyticsInstances, {
  getFallbackAnalyticsInstanceId
} from '../../common/analyticsInstances';
import { useEnterprises } from '../../common/contexts/enterprises';

const TestPage: React.FC = () => {
  const { login, user: authUser } = useAuthContext();
  const { activeEnterprise } = useEnterprises();
  const { loading, error, test, issues, manifests, manifestRerunGroupsByType } =
    useTest();
  const { setContents } = useGlobalToast();
  const connections = useIntegrationConnections(
    activeEnterprise?.id,
    authUser?.token
  );
  const axeAuditorIntegrationAnalytics =
    analyticsInstances[
      getFallbackAnalyticsInstanceId(ProductSlugs.axeAuditorIntegration)
    ];

  const axeAuditorIntegrationConn = connections.data?.find(
    conn => conn.product_slug === ProductSlugs.axeAuditorIntegration
  );

  const [showSendToAuditorModal, setShowSendToAuditorModal] =
    useState<boolean>(false);

  const [overrideTestDataFlag, setOverrideTestDataFlag] =
    useState<boolean>(false);

  const [
    disableOverrideTestDataFlagCheckbox,
    setDisableOverrideTestDataFlagCheckbox
  ] = useState<boolean>(true);

  const [messageToBeShownOnModal, setMessageToBeShownOnModal] =
    useState<string>('');

  const [sendTestModalShowLoader, setSendTestModalShowLoader] =
    useState<boolean>(false);

  const universalIssues: UniversalIssueWithScreenshot[] = useMemo(() => {
    return issues.map(issue => ({
      ...mapAxeExtensionIssueToUniversalIssue(mapIssueToSavedIssue(issue)),
      screenshotId: issue.screenshot_id,
      boundingBox: issue.bounding_box
    }));
  }, [issues]);

  const {
    filteredIssues: filteredUniversalIssues,
    totalIssues: totalUniversalIssues,
    filters,
    updateFilter
  } = useIssuesFilter(universalIssues);

  /* 
    The export code still expects TestRecordIssues, but the filtering code returns UniversalIssues.
    For now, we filter TestRecordIssues by the filtered universal issues for backwards compatibility.
    This is a temporary solution that we will remove once the export code is converted to universal issues. 
  */
  const filteredIssues = useMemo(
    () => getIssuesWithUniversalMatches(issues, filteredUniversalIssues),
    [issues, filteredUniversalIssues]
  );

  const { t } = useTranslation();
  const narrow = useMediaQuery('(max-width: 45rem)');
  const analytics = useAxeDevtoolsProAnalytics();
  const {
    ready: analyticsReady,
    data: analyticsData,
    testId
  } = useSharedTestAnalyticsData();

  useEffect(() => {
    if (analyticsReady) {
      analytics.viewSharedTest(analyticsData);
    }
  }, [analyticsReady, testId]);

  const onCopy = () => {
    if (analyticsReady) {
      analytics.sharedTestCopyUrl(analyticsData);
    }
  };

  const handleExportClick = () => {
    if (!test || !manifestRerunGroupsByType) {
      return;
    }

    const currentManifests = Object.entries(manifestRerunGroupsByType).reduce(
      (acc, current) => {
        const [type, rerunGroups] = current;
        acc[type as keyof Manifests] = rerunGroups.map(rerunGroup =>
          manifestUtils.getCurrentManifestForRerunGroup(rerunGroup)
        );
        return acc;
      },
      {} as Manifests
    );

    universalExport({
      filename: `${getFriendlyFilename(test.url, test.name)}.json`,
      test: {
        id: test.id,
        metadata: {
          axeVersion: (test.metadata?.axeVersion as string) ?? ''
        },
        url: test.url,
        skipped_guides: test.skipped_guides ?? [],
        // This type isn't typed correctly
        // see: https://github.com/dequelabs/walnut/issues/6902
        manual_tests_completed_at: test.manual_tests_completed_at?.toString()
      },
      manifests: currentManifests,
      issues: (filteredIssues as unknown as Array<Record<string, unknown>>).map(
        issue =>
          mapIssueToSavedIssue(issue, {
            shareURL:
              issue.shared_with || test.shared_with
                ? `https://${location.host}/issues/${issue.id}`
                : undefined
          })
      ),
      additionalProperties: {
        startDate: test.created_at,
        endDate: test.updated_at ?? test.created_at,
        bestPracticesEnabled: filters.configBestPractices === 'enabled',
        // TODO: Once the experimental toggle is available, it should be passed
        // as an additional property to export
        experimentalEnabled: false,
        source: {
          productName: 'axe-devtools-html',
          productComponentName: 'web-ui',
          productVersion: pkg.version
        },
        standard:
          filters.configAccessibilityStandard === 'all'
            ? rulesets['wcag22aaa'].name
            : rulesets[filters.configAccessibilityStandard].name,
        engine: 'axe-core',
        baseScreenshotURL: getApiServerUrl()
      }
    });

    if (analyticsReady) {
      analytics.sharedTestOverviewExport();
    }
  };

  if (loading) {
    return (
      <>
        <PageTitle title={t('Loading test')} />
        <Loader label={t('Loading test...')} />
      </>
    );
  }

  const handleLogin = (): void => {
    login();
  };

  if (error && isNotFoundError(error)) {
    return (
      <div className={styles.wrap}>
        <PageTitle title={t('Shared test record not found')} />
        <div className={styles.header}>
          <h1>{t('Shared test record not found')}</h1>
        </div>
        <p>
          <Trans>We were unable to find this test record.</Trans>{' '}
          <Trans>
            You may need to{' '}
            <Button onClick={handleLogin} variant="link">
              sign in
            </Button>{' '}
            or ensure you have access to this test.
          </Trans>
        </p>
      </div>
    );
  } else if (error || !test || !manifests || !manifestRerunGroupsByType) {
    return (
      <div className={styles.wrap}>
        <PageTitle title={t('Failed to load shared test record')} />
        <div className={styles.header}>
          <h1>{t('Failed to load shared test record')}</h1>
        </div>
        <p>
          <Trans>We were unable to load the test record.</Trans>{' '}
          <Trans>
            Please try again, and if the issue persists, contact{' '}
            <Link href="mailto:helpdesk@deque.com">helpdesk@deque.com</Link>.
          </Trans>
        </p>
      </div>
    );
  }

  const testName = test.name || t('Test');
  const pageTitle = t('View {{testName}}', { testName });
  const searchParams = getSearchParamsFromFilters(getBaseFilters(filters));
  const testLink = `${window.location.origin}${window.location.pathname}?${searchParams}`;

  const handleSendToAuditorButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
    axeAuditorIntegrationAnalytics.integrationTestSendClick({
      integration: 'axe-auditor',
      source: 'web-app'
    });
    setShowSendToAuditorModal(true);
  };

  const renderSendToAuditorButton = () => {
    const isAxeAuditorIntegrationFeatureFlagEnabled = useFeatureFlagState(
      'axe_auditor_integration_v1'
    );
    const axeAuditorIntegrationSubscription = useCurrentSubscription({
      productSlug: ProductSlugs.axeAuditorIntegration
    });
    const isAxeAuditorIntegrationSubscriptionActive =
      axeAuditorIntegrationSubscription
        ? axeAuditorIntegrationSubscription.purchase_state === 'paid'
        : false;
    const isLoggedInUserTestCreator = test.creator_id === authUser?.id;

    return (
      isAxeAuditorIntegrationFeatureFlagEnabled &&
      isAxeAuditorIntegrationSubscriptionActive &&
      axeAuditorIntegrationConn?.id &&
      isLoggedInUserTestCreator && (
        <Button
          variant="secondary"
          onClick={handleSendToAuditorButtonClick}
          thin
        >
          {t('Send to Auditor')}
        </Button>
      )
    );
  };

  const handleSendToAuditor = async (testUnitId: string) => {
    try {
      if (overrideTestDataFlag) {
        axeAuditorIntegrationAnalytics.integrationTestSendOverride({
          integration: 'axe-auditor',
          source: 'web-app'
        });
      }
      setSendTestModalShowLoader(true);
      await sendTest({
        testId: test.id,
        testUnitId: testUnitId,
        overrideIssuesFlag: overrideTestDataFlag,
        token: authUser?.token,
        enterpriseId: activeEnterprise?.id,
        standard:
          filters.configAccessibilityStandard === 'all'
            ? rulesets['wcag22aaa'].name
            : rulesets[filters.configAccessibilityStandard].name,
        connectionId: axeAuditorIntegrationConn?.id,
        filters
      });
      axeAuditorIntegrationAnalytics.integrationTestSendSuccess({
        integration: 'axe-auditor',
        source: 'web-app'
      });

      setContents(
        t('Issues have been successfully sent to axe Auditor for this test.')
      );
      setShowSendToAuditorModal(false);
      setMessageToBeShownOnModal('');
      setDisableOverrideTestDataFlagCheckbox(true);
      setOverrideTestDataFlag(false);
    } catch (err) {
      const errorStatusCode = (err as HttpError).status;
      switch (errorStatusCode) {
        case 409:
          axeAuditorIntegrationAnalytics.integrationTestSendConflict({
            integration: 'axe-auditor',
            source: 'web-app'
          });
          setMessageToBeShownOnModal(
            t(
              'Test data has already been sent to the provided test unit. Would you like to override the existing data?'
            )
          );
          setDisableOverrideTestDataFlagCheckbox(false);
          break;
        case 401:
          axeAuditorIntegrationAnalytics.integrationTestSendFail({
            integration: 'axe-auditor',
            source: 'web-app'
          });
          setContents(
            t('Authentication failed. Please contact your administrator.')
          );
          setShowSendToAuditorModal(false);
          break;
        case 500:
        case 504:
          axeAuditorIntegrationAnalytics.integrationTestSendFail({
            integration: 'axe-auditor',
            source: 'web-app'
          });
          setContents(t('Internal server error. Please try again later.'));
          setShowSendToAuditorModal(false);
          break;
        case 404:
          axeAuditorIntegrationAnalytics.integrationTestSendNotFound({
            integration: 'axe-auditor',
            source: 'web-app'
          });
          setContents(
            t(
              'Test Unit not found. Please check the Test Unit Id and try again later.'
            )
          );
          setShowSendToAuditorModal(false);
          break;
        case 400:
          axeAuditorIntegrationAnalytics.integrationTestSendFail({
            integration: 'axe-auditor',
            source: 'web-app'
          });
          setContents(t('Invalid request. Please try again later.'));
          setShowSendToAuditorModal(false);
          break;
        default:
          axeAuditorIntegrationAnalytics.integrationTestSendFail({
            integration: 'axe-auditor',
            source: 'web-app'
          });
          setContents(t('An unknown error occurred. Please try again later.'));
          setShowSendToAuditorModal(false);
          break;
      }
    }
    setSendTestModalShowLoader(false);
  };

  const handleSendTestModalClose = () => {
    setShowSendToAuditorModal(false);
    setMessageToBeShownOnModal('');
    setDisableOverrideTestDataFlagCheckbox(true);
  };

  return (
    <>
      <div className={styles.banner}>
        <ShareTestBetaFeatureToast />
      </div>
      <div className={styles.wrap}>
        <PageTitle title={pageTitle} />
        <div className={styles.header}>
          <h1>{pageTitle}</h1>
          <CopyToClipboard
            onCopy={onCopy}
            text={testLink}
            confirmationMessage={t(
              'A link for {{testName}} has been copied to clipboard',
              {
                testName
              }
            )}
          >
            <IconButton
              icon="link"
              label={t('Copy link for {{testName}} to clipboard', { testName })}
              tooltipProps={{
                placement: 'bottom'
              }}
            />
          </CopyToClipboard>
          <IconButton
            icon="download"
            label={t('Export JSON Saved Test and Issues for {{ testName }}', {
              testName
            })}
            aria-disabled={!test || !manifestRerunGroupsByType}
            onClick={handleExportClick}
          />
          {renderSendToAuditorButton()}
          {showSendToAuditorModal && (
            <SendTestModal
              showSendTestModal={showSendToAuditorModal}
              onClose={handleSendTestModalClose}
              handleSendTest={handleSendToAuditor}
              overrideTestDataFlag={overrideTestDataFlag}
              setOverrideTestDataFlag={setOverrideTestDataFlag}
              disableOverrideTestDataFlagCheckbox={
                disableOverrideTestDataFlagCheckbox
              }
              messageToBeShown={messageToBeShownOnModal}
              showLoader={sendTestModalShowLoader}
            />
          )}
        </div>
        <TestDetails
          test={test}
          issues={filteredUniversalIssues}
          totalIssues={totalUniversalIssues}
          filters={filters}
          updateFilter={updateFilter}
          narrow={narrow}
          manifests={manifests}
          manifestRerunGroupsByType={manifestRerunGroupsByType}
        />
      </div>
    </>
  );
};

export default TestPage;
